import { literals } from "@/constants/literals";
import { useState, useMemo, useCallback } from "react";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { Icon } from "@/components/icons/Icon";
import TagLabel from "./TagLabel";
import { ADVANCED_SEARACH, MORE_OPTIONS } from "./utils";
import { formatDateDDMmmYYYY } from "@/lib/dates";
import type { PhotosFilters } from "@/types/types.d";
import BasicPhotosSearcher from "./BasicPhotosSearcher";
import AdvancedPhotosSearcher from "./AdvancedPhotosSearcher";

type PhotosSearcherMobileProps = {
  initialFilters?: any;
  onSearch: (filters: any) => void;
};

const PhotosSearcherMobile = ({
  initialFilters = {},
  onSearch,
}: PhotosSearcherMobileProps) => {
  const [showAdvancedSearcher, setShowAdvancedSearcher] = useState(false);
  const [showBasicSearcher, setShowBasicSearcher] = useState(true);

  const [filters, setFilters] = useState<PhotosFilters>(initialFilters);

  const selectArea = useCallback(
    (newArea: string) => {
      if (newArea !== filters.area) {
        const newFilters: PhotosFilters = {
          area: newArea,
          step: "",
        };
        if (newArea !== "Packaging") {
          if (
            filters.iberiaPackagingOut &&
            filters.iberiaPackagingOut.length > 0
          )
            newFilters.iberiaPackagingOut = "";
          if (filters.ownPackagingOut && filters.ownPackagingOut.length > 0)
            newFilters.ownPackagingOut = "";
        }
        setFilters((activeFilters) => ({
          ...activeFilters,
          ...newFilters,
        }));
      }
    },
    [filters.area, filters.iberiaPackagingOut, filters.ownPackagingOut],
  );

  const onlyOneDate = useMemo(() => {
    return (filters.fechaDesde && !filters.fechaHasta) ||
      (!filters.fechaDesde && filters.fechaHasta)
      ? true
      : false;
  }, [filters.fechaDesde, filters.fechaHasta]);

  const hasInvalidDateRange = useMemo(() => {
    return filters.fechaDesde &&
      filters.fechaHasta &&
      filters.fechaDesde > filters.fechaHasta
      ? true
      : false;
  }, [filters.fechaDesde, filters.fechaHasta]);

  const noCorrectSapOrder = useMemo(() => {
    return filters.sapWorkOrder?.length &&
      (filters.sapWorkOrder.length < 10 || filters.sapWorkOrder.length > 12)
      ? true
      : false;
  }, [filters.sapWorkOrder]);

  const noCorrectCustomer = useMemo(() => {
    return filters.customer?.length &&
      (filters.customer.length < 3 || filters.customer.length > 6)
      ? true
      : false;
  }, [filters.customer]);

  const noCorrect9r1 = useMemo(() => {
    return filters.document9r1?.length && filters.document9r1.length !== 10
      ? true
      : false;
  }, [filters.document9r1]);

  const noValidDataToSearch = useMemo(() => {
    return onlyOneDate ||
      hasInvalidDateRange ||
      noCorrectCustomer ||
      noCorrect9r1 ||
      noCorrectSapOrder
      ? true
      : false;
  }, [
    onlyOneDate,
    hasInvalidDateRange,
    noCorrectCustomer,
    noCorrect9r1,
    noCorrectSapOrder,
  ]);

  const handleSearch = async () => {
    if (!noValidDataToSearch) {
      setShowBasicSearcher(false);
      setShowAdvancedSearcher(false);
      const newFilters = Object.entries(filters).map(([key, value]) => {
        const trimmedValue = (value ?? "").trim();
        return [key, trimmedValue === "" ? undefined : trimmedValue] as const;
      });
      onSearch(Object.fromEntries(newFilters));
    }
  };

  const getTagValueToShow = (label: string, value: string | boolean) => {
    if (label === literals.dateFrom || label === literals.dateTo) {
      return formatDateDDMmmYYYY(value as string);
    }
    if (value === "true" || value === true) {
      return literals.yes;
    }
    if (value === "false" || value === false) {
      return literals.no;
    }
    return value;
  };

  const changeFilterCriteria = useCallback(
    (value: string | undefined | null, field: string) => {
      setFilters((previousFilters) => ({
        ...previousFilters,
        [field]: value,
      }));
    },
    [],
  );

  return (
    <div
      data-testid="photos-admin-page"
      className="w-full grid grid-rows-[1fr_auto] h-full"
    >
      <div className="w-full bg-[#FAFAFA] rounded-md p-6 overflow-y-auto">
        {/* Header: "Búsqueda avanzada" ---- line ---- chevron */}
        <button
          type="button"
          className="text-sm text-gray-700 w-full flex items-center gap-3 pb-4"
          onClick={() => setShowBasicSearcher((v) => !v)}
        >
          <span className=" mr-2 font-ib-bold">{literals.basicSearch}</span>
          <div className="flex-1 h-px bg-gray-300" />
          <span className="underline mr-2">
            {showBasicSearcher ? literals.hideOptions : literals.showOptions}
          </span>
          <Icon name={showBasicSearcher ? "chevronUp" : "chevronDown"} />
        </button>
        {/* Filters */}
        {!showBasicSearcher && (
          <div className="flex">
            <div className="flex flex-wrap gap-2">
              {Object.entries(ADVANCED_SEARACH).map(([key, value]) => {
                const filterValue = filters[key as keyof typeof filters];
                if (filterValue) {
                  return (
                    <TagLabel
                      key={key}
                      label={value || key}
                      value={getTagValueToShow(value, filterValue)}
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
        {showBasicSearcher && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-5">
            <BasicPhotosSearcher
              filters={filters}
              noCorrectSapOrder={noCorrectSapOrder}
              noCorrect9r1={noCorrect9r1}
              noCorrectCustomer={noCorrectCustomer}
              selectArea={selectArea}
              changeFilterCriteria={changeFilterCriteria}
            />
          </div>
        )}
        <div className="flex mt-6 w-full">
          <button
            type="button"
            className="text-sm text-gray-700 w-full flex items-center gap-3 pb-4"
            onClick={() => setShowAdvancedSearcher((v) => !v)}
          >
            <span className="font-ib-bold">{literals.advancedSearch}</span>
            <div className="flex-1 h-px bg-gray-300" />
            <span className="underline">
              {showAdvancedSearcher
                ? literals.hideOptions
                : literals.showOptions}
            </span>
            <Icon name={showAdvancedSearcher ? "chevronUp" : "chevronDown"} />
          </button>
        </div>
        {!showAdvancedSearcher && (
          <div className="flex">
            <div className="flex flex-wrap gap-2">
              {Object.entries(MORE_OPTIONS).map(([key, value]) => {
                const filterValue = filters[key as keyof typeof filters];
                if (filterValue) {
                  return (
                    <TagLabel
                      key={key}
                      label={value || key}
                      value={getTagValueToShow(value, filterValue)}
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
        {showAdvancedSearcher && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-5">
            <AdvancedPhotosSearcher
              filters={filters}
              hasInvalidDateRange={hasInvalidDateRange}
              changeFilterCriteria={changeFilterCriteria}
            />
          </div>
        )}
      </div>
      <div className=" pt-4 border-t">
        <PrimaryButton
          icon="searchLens"
          size="small"
          label={literals.search}
          colour="red"
          type="button"
          onClick={handleSearch}
          disabled={noValidDataToSearch}
        />
      </div>
    </div>
  );
};

export default PhotosSearcherMobile;
